/**
 * CadenceTentativeTab — Tier 1 (Tentative Cadence) viewer.
 *
 * Shows the coarse "tentative" fire rows projected across each rule's whole
 * tenure (SCH_fire_queue, status='tentative'). Lets the operator run the
 * tentative enqueue and clear it for repeatable testing. User is NOT resolved
 * at this tier — only geography + accountable authority.
 *
 * Server-paginated: rows are fetched one page at a time (LIMIT/OFFSET + a
 * COUNT-backed total) via listFiresPaged — a whole-tenure projection can run to
 * thousands of rows per tenant, so the client never holds the full set.
 */
import React, { useEffect, useState } from 'react';
import { Play, Trash2, RefreshCw } from 'lucide-react';
import Button from '../common/Button';
import Badge from '../common/Badge';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import Toast, { ToastMessage } from '../common/Toast';
import { statusVariant } from '../../utils/statusVariant';
import { confirmWarning, swalPermission } from '../common/confirm';
import extractApiError from '../../utils/apiError';
import { listFiresPaged, runEnqueueTentative, purgeFires, TentativeFire } from '../../services/cadenceService';
import { usePermissions } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import { SCH_GLOBAL_CADENCE } from './cadenceModuleTypes';

const CadenceTentativeTab: React.FC = () => {
  const { formatDate } = useEntitySettings();
  // Standalone Cadence Engine → gated by the global cadence activity. Running /
  // clearing the tentative tier is a mutation (the backend run endpoints call
  // _enforce_cadence_modify), so it needs view+modify.
  const { check, loading: permLoading } = usePermissions();
  const canModify = check(SCH_GLOBAL_CADENCE, 'view+modify').allowed;
  const canView = check(SCH_GLOBAL_CADENCE, 'view').allowed;
  const [fires, setFires] = useState<TentativeFire[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [includeDrafts, setIncludeDrafts] = useState(true);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  useEffect(() => { setPage(1); }, [pageSize]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoading(true);
    listFiresPaged('tentative', { page, limit: pageSize })
      .then(res => {
        if (cancelled) return;
        setFires(res.items);
        setTotalRecords(res.total);
      })
      .catch(err => {
        if (cancelled) return;
        setFires([]);
        setTotalRecords(0);
        setToast({ type: 'error', message: extractApiError(err, 'Failed to load tentative fires.') });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, pageSize, reloadNonce, canView]);

  const refresh = () => setReloadNonce(n => n + 1);
  const reloadFirstPage = () => { setPage(1); setReloadNonce(n => n + 1); };

  const handleRun = async () => {
    if (!canModify) { swalPermission('You do not have permission to run cadence.'); return; }
    setBusy(true);
    try {
      const r = await runEnqueueTentative(undefined, includeDrafts);
      setToast({ type: 'success', message: `Tentative run: ${r.tentative_inserted} new rows from ${r.rules_processed} rule(s).` });
      reloadFirstPage();
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Tentative run failed.') });
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async () => {
    if (!canModify) { swalPermission('You do not have permission to modify cadence.'); return; }
    const ok = await confirmWarning({
      title: 'Clear tentative cadence?',
      text: 'Deletes all tentative (Tier 1) rows for this entity. Re-run any time to regenerate.',
      confirmText: 'Yes, Clear',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await purgeFires('tentative');
      setToast({ type: 'success', message: `Deleted ${r.queue_deleted} tentative row(s).` });
      reloadFirstPage();
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Clear failed.') });
    } finally {
      setBusy(false);
    }
  };

  if (!permLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to cadence." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full min-h-[500px]">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.Checkbox
            label="Include drafts"
            checked={includeDrafts}
            onChange={setIncludeDrafts}
            noMargin
            inline
            data-testid="CHK_tentative_include_drafts"
          />
          <Button variant="secondary-outline" icon={RefreshCw} onClick={refresh} data-testid="BTN_tentative_refresh">
            Refresh
          </Button>
          <Button variant="delete-outline" icon={Trash2} disabled={busy} onClick={handleClear} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_tentative_clear">
            Clear Tentative
          </Button>
          <Button variant="fill" icon={Play} disabled={busy} onClick={handleRun} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_tentative_run">
            {busy ? 'Running…' : 'Run Tentative Cadence'}
          </Button>
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Rule</th>
                    <th className="table-list-th">Occurrence</th>
                    <th className="table-list-th">Period</th>
                    <th className="table-list-th">Due Date</th>
                    <th className="table-list-th">Geography</th>
                    <th className="table-list-th">Authority</th>
                    <th className="table-list-th table-list-cell--status">Status</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && (
                    <Skeleton.TableRows cols={['2xs', 'sm', '2xs', 'sm', 'sm', 'md', 'md', 'pill']} />
                  )}

                  {!loading && fires.length === 0 && (
                    <tr>
                      <td colSpan={8} className="table-list-cell">
                        <NoDataFound
                          title="No tentative cadence yet"
                          message="Run the tentative cadence to project every active rule's whole-tenure occurrences across its geographies."
                        />
                      </td>
                    </tr>
                  )}

                  {!loading && fires.map((f, idx) => (
                    <tr key={f.id} className="table-list-row" data-testid={`ROW_tentative_${f.id}`}>
                      <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                      <td className="table-list-cell">
                        <span className="table-list-cell-title">#{f.rule_id}</span>{' '}
                        <span className="table-list-cell-sub">v{f.rule_version}</span>
                      </td>
                      <td className="table-list-cell">{f.occurrence_number}</td>
                      <td className="table-list-cell">{f.period_token || '—'}</td>
                      <td className="table-list-cell">{formatDate(f.scheduled_for)}</td>
                      <td className="table-list-cell">{f.geography_name || `loc ${f.scope_element_id ?? '—'}`}</td>
                      <td className="table-list-cell">{f.authority_name || (f.authority_id ? `auth ${f.authority_id}` : '—')}</td>
                      <td className="table-list-cell table-list-cell--status">
                        <Badge variant={statusVariant(f.status)} size="sm">{f.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="tentative rows"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadenceTentativeTab;
